/**
 * PAY2LOSE - Game Configuration
 * 
 * All balancing values are centralized here for easy tuning.
 * Adjust these values to make the game more/less aggressive.
 */

export const GAME_CONFIG = {
    // ============================================
    // LOADING SCREEN
    // ============================================
    loading: {
        baseTime: 60,           // Seconds to load naturally
        adBoost: 0.20,          // +20% progress per ad
        instantCost: 0.99,      // € to skip loading
    },

    // ============================================
    // TUTORIAL
    // ============================================
    tutorial: {
        steps: 5,               // Number of tutorial steps
        waitTimes: [2, 3, 5, 3, 2], // Seconds per step (very short)
        freebieGems: 100,       // Starting bonus gems
        freebieGold: 500,       // Starting bonus gold
    },

    // ============================================
    // MIDGAME
    // ============================================
    midgame: {
        baseWaitTimeSeconds: 60,     // 1 minute base
        waitTimeMultiplier: 1.5,     // Each level increases wait
        adLimitPerHour: 5,           // Max ads per hour
        adWaitReduction: 0.2,        // 20% time reduction per ad
    },

    // ============================================
    // ENDGAME / RANKING
    // ============================================
    endgame: {
        startingRank: 100,           // Player starts at rank 100
        targetRank: 1,               // Goal: Rank 1
        powerScaling: 1.08,          // +8% power per rank
        topPlayerPower: 999999,      // Rank #1 power level
        rankUpPowerCost: 250,        // Base power needed per rank
    },

    // ============================================
    // ECONOMY / PRICING
    // ============================================
    pricing: {
        // Gem Packs (simulated €)
        gem100: 1.99,
        gem500: 7.99,
        gem1000: 14.99,
        gem2000: 24.99,
        gem5000: 49.99,

        // Special Bundles
        starterBundle: 4.99,
        megaBundle: 9.99,
        whaleBundle: 99.99,

        // Subscriptions
        vip1Day: 4.99,
        vip7Days: 19.99,
        vip30Days: 49.99,

        // Battle Pass
        battlePassPremium: 9.99,
        battlePassLevelCost: 50,  // Gems per level skip
    },

    // ============================================
    // ENERGY SYSTEM
    // ============================================
    energy: {
        max: 50,                    // Maximum energy
        regenMinutes: 5,            // Minutes per 1 energy
        actionCost: 5,              // Energy per action
        adRefill: 10,               // Energy from watching ad
        fullRefillCostGems: 50,     // Gems for full refill
    },

    // ============================================
    // GACHA / LOOTBOX
    // ============================================
    gacha: {
        pullCostGems: 100,          // Gems per pull
        tenPullDiscount: 0.10,      // 10% discount on 10-pull
        pityCount: 100,             // Guaranteed legendary at 100 pulls

        dropRates: {
            common: 0.70,       // 70%
            uncommon: 0.20,     // 20%
            rare: 0.08,         // 8%
            epic: 0.019,        // 1.9%
            legendary: 0.001,   // 0.1%
        },
    },

    // ============================================
    // REFERRAL SYSTEM
    // ============================================
    referral: {
        stages: [
            {
                requirement: 'registered',
                label: 'Freund registriert',
                gemsReward: 50,
            },
            {
                requirement: 'tutorial_complete',
                label: 'Tutorial abgeschlossen',
                gemsReward: 100,
                waitReduction: 0.10,  // 10% less wait time
            },
            {
                requirement: 'rank_50',
                label: 'Rang #50 erreicht',
                gemsReward: 250,
                rareGear: true,
            },
            {
                requirement: 'first_purchase_5',
                label: 'Erster Kauf (5€+)',
                gemsReward: 500,
                vipHours: 24,
            },
            {
                requirement: 'whale_50',
                label: 'Whale-Status (50€+)',
                gemsReward: 2000,
                exclusiveTitle: 'Pyramid Master',
            },
        ],
    },

    // ============================================
    // DAILY REWARDS
    // ============================================
    daily: {
        rewards: [
            { day: 1, gems: 10 },
            { day: 2, gems: 25 },
            { day: 3, gems: 50 },
            { day: 4, gems: 75 },
            { day: 5, gems: 100 },
            { day: 6, gems: 150 },
            { day: 7, gems: 300, bonus: 'rare_chest' },
        ],
        streakResetPenalty: true,   // Missing a day resets streak
        adSkipDays: 1,              // Ads can skip 1 day forward
        vipMultiplier: 2,           // VIP gets 2x rewards
    },

    // ============================================
    // BATTLE PASS
    // ============================================
    battlePass: {
        seasonDays: 30,             // Season length
        maxLevel: 50,               // Max battle pass level
        xpPerLevel: 1000,           // XP needed per level

        freeTrackValue: 500,        // Total gems in free track
        premiumTrackValue: 5000,    // Total gems in premium track
    },

    // ============================================
    // EVENTS
    // ============================================
    events: {
        flashSaleDurationHours: 4,
        weekendEventMultiplier: 2,
        limitedItemCount: 10,       // "Only X left!"
    },

    // ============================================
    // SECOND CHANCE POPUPS
    // ============================================
    popups: {
        shopExitChance: 0.8,        // 80% chance to show popup
        battleLossChance: 1.0,      // 100% after losing
        longWaitThreshold: 1800,    // 30 minutes

        declineTexts: [
            'Nein, ich hasse gute Deals',
            'Nein, ich verliere gerne',
            'Nein, ich warte lieber',
            'Nein, Sparen ist nichts für mich',
            'Nein, ich mag es ineffizient',
        ],
    },
} as const

// Type exports for the config
export type GameConfig = typeof GAME_CONFIG
export type GachaTier = keyof typeof GAME_CONFIG.gacha.dropRates
